import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineChevronUp,
  HiOutlineChevronDown,
  HiOutlineRefresh,
  HiOutlineClipboardCheck,
  HiOutlineBell,
  HiCheck,
} from 'react-icons/hi';
import { Card, Button, Input, Modal, Spinner, Tooltip, ConfirmDialog } from '../../components/UI';
import { visitsAPI } from '../../services/api';

const PRESET_COLORS = [
  '#16A34A', '#059669', '#0891B2', '#2563EB', '#4F46E5', '#7C3AED',
  '#DB2777', '#DC2626', '#EA580C', '#D97706', '#65A30D', '#6B7280',
];

const Switch = ({ checked, onChange, label, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={clsx(
      'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors disabled:opacity-50',
      checked ? 'bg-success-500' : 'bg-secondary-300'
    )}
  >
    <span
      className={clsx(
        'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
        checked && 'translate-x-5'
      )}
    />
  </button>
);

/**
 * Settings → Visits → Visit Outcomes
 * The options a telecaller picks when ending a visit ("How did the visit
 * go?"). Managed like pipeline stages: add, rename, colour, reorder,
 * deactivate, delete. "Needs follow-up date" makes the app ask for the next
 * follow-up and creates it in Follow-ups.
 */
const VisitOutcomes = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null); // outcome being edited, or {} for new
  const [form, setForm] = useState({ label: '', color: PRESET_COLORS[0], requiresFollowUp: false, isActive: true });
  const [saving, setSaving] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const { data: outcomes = [], isLoading } = useQuery({
    queryKey: ['visit-outcomes'],
    queryFn: async () => (await visitsAPI.getOutcomes()).data?.data || [],
  });

  const apply = async (fn, key) => {
    setBusyKey(key);
    try {
      const res = await fn();
      queryClient.setQueryData(['visit-outcomes'], res.data?.data || []);
      toast.success(res.data?.message || 'Saved');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save');
      return false;
    } finally {
      setBusyKey(null);
    }
  };

  const openEditor = (outcome) => {
    setEditing(outcome || {});
    setForm(
      outcome
        ? { label: outcome.label, color: outcome.color, requiresFollowUp: outcome.requiresFollowUp, isActive: outcome.isActive }
        : { label: '', color: PRESET_COLORS[outcomes.length % PRESET_COLORS.length], requiresFollowUp: false, isActive: true }
    );
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) {
      toast.error('Enter an outcome name');
      return;
    }
    setSaving(true);
    const ok = await apply(
      () => (editing?.key ? visitsAPI.updateOutcome(editing.key, form) : visitsAPI.createOutcome(form)),
      editing?.key || 'new'
    );
    setSaving(false);
    if (ok) setEditing(null);
  };

  const move = (index, dir) => {
    const keys = outcomes.map((o) => o.key);
    const target = index + dir;
    [keys[index], keys[target]] = [keys[target], keys[index]];
    apply(() => visitsAPI.reorderOutcomes(keys), outcomes[index].key);
  };

  const activeCount = outcomes.filter((o) => o.isActive).length;

  return (
    <Card padding="p-0" className="overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 px-4 lg:px-5 py-4 border-b border-secondary-100">
        <div className="flex items-start gap-3">
          <HiOutlineClipboardCheck className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-secondary-900">Visit Outcomes</p>
            <p className="text-xs text-secondary-500 mt-0.5">
              Options shown in the app under "How did the visit go?" when a visit ends. Order here is the order in the app.
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button size="sm" variant="ghost" icon={<HiOutlineRefresh className="w-4 h-4" />} onClick={() => setConfirmReset(true)}>
            Reset
          </Button>
          <Button size="sm" icon={<HiOutlinePlus className="w-4 h-4" />} onClick={() => openEditor(null)}>
            Add Outcome
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner size="lg" />
        </div>
      ) : (
        <ul className="divide-y divide-secondary-100">
          {outcomes.map((o, index) => (
            <li
              key={o.key}
              className={clsx('flex items-center gap-3 px-4 lg:px-5 py-3 hover:bg-secondary-50/60', !o.isActive && 'opacity-60')}
            >
              <span className="w-7 h-7 rounded-full bg-secondary-100 text-secondary-600 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                {index + 1}
              </span>
              <span className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={{ backgroundColor: o.color }} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-sm font-semibold text-secondary-900">{o.label}</span>
                  {o.requiresFollowUp && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-primary-50 text-primary-700">
                      <HiOutlineBell className="w-3 h-3" />
                      Needs follow-up date
                    </span>
                  )}
                  {!o.isActive && (
                    <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-secondary-100 text-secondary-600">Inactive</span>
                  )}
                </div>
                <p className="text-xs text-secondary-500 mt-0.5">
                  {o.visitsCount} visit{o.visitsCount === 1 ? '' : 's'}
                </p>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                {busyKey === o.key && <Spinner size="sm" className="mr-1" />}
                <div className="flex flex-col">
                  <button
                    type="button"
                    aria-label={`Move ${o.label} up`}
                    disabled={index === 0 || !!busyKey}
                    onClick={() => move(index, -1)}
                    className="p-0.5 rounded text-secondary-400 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-30 disabled:hover:bg-transparent"
                  >
                    <HiOutlineChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${o.label} down`}
                    disabled={index === outcomes.length - 1 || !!busyKey}
                    onClick={() => move(index, 1)}
                    className="p-0.5 rounded text-secondary-400 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-30 disabled:hover:bg-transparent"
                  >
                    <HiOutlineChevronDown className="w-4 h-4" />
                  </button>
                </div>
                <Tooltip position="left" text={o.isActive ? 'Active — shown in the app' : 'Inactive — hidden in the app'}>
                  <span className="inline-flex px-1.5">
                    <Switch
                      checked={o.isActive}
                      label={`${o.label} active`}
                      disabled={!!busyKey || (o.isActive && activeCount === 1)}
                      onChange={(v) => apply(() => visitsAPI.updateOutcome(o.key, { isActive: v }), o.key)}
                    />
                  </span>
                </Tooltip>
                <Tooltip position="left" text="Edit">
                  <button
                    type="button"
                    aria-label={`Edit ${o.label}`}
                    onClick={() => openEditor(o)}
                    className="p-2 rounded-lg text-secondary-400 hover:text-primary-600 hover:bg-primary-50"
                  >
                    <HiOutlinePencil className="w-4 h-4" />
                  </button>
                </Tooltip>
                <Tooltip position="left" text="Delete">
                  <button
                    type="button"
                    aria-label={`Delete ${o.label}`}
                    disabled={o.isActive && activeCount === 1}
                    onClick={() => setToDelete(o)}
                    className="p-2 rounded-lg text-secondary-400 hover:text-danger-600 hover:bg-danger-50 disabled:opacity-30"
                  >
                    <HiOutlineTrash className="w-4 h-4" />
                  </button>
                </Tooltip>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Add / edit */}
      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title={editing?.key ? 'Edit Outcome' : 'Add Outcome'} size="sm">
        <form onSubmit={save} className="space-y-4">
          <Input
            label="Name"
            required
            maxLength={40}
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder="e.g. Quotation Requested"
            autoFocus
          />

          <div className="space-y-1.5">
            <p className="text-sm font-semibold text-secondary-700">Colour</p>
            <div className="flex flex-wrap items-center gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Colour ${c}`}
                  aria-pressed={form.color === c}
                  onClick={() => setForm((f) => ({ ...f, color: c }))}
                  className="w-7 h-7 rounded-full flex items-center justify-center ring-offset-2 transition-transform hover:scale-110"
                  style={{ backgroundColor: c, boxShadow: form.color === c ? `0 0 0 2px white, 0 0 0 4px ${c}` : undefined }}
                >
                  {form.color === c && <HiCheck className="w-4 h-4 text-white" />}
                </button>
              ))}
              <label className="relative w-7 h-7 rounded-full border-2 border-dashed border-secondary-300 flex items-center justify-center cursor-pointer hover:border-secondary-400" title="Custom colour">
                <HiOutlinePlus className="w-3.5 h-3.5 text-secondary-400" />
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value.toUpperCase() }))}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  aria-label="Custom colour"
                />
              </label>
            </div>
          </div>

          <div className="flex items-start justify-between gap-3 rounded-xl border border-secondary-200 p-3">
            <div>
              <p className="text-sm font-semibold text-secondary-800">Needs follow-up date</p>
              <p className="text-xs text-secondary-500 mt-0.5">
                The app asks for the next follow-up date and adds it to Follow-ups.
              </p>
            </div>
            <Switch
              checked={form.requiresFollowUp}
              label="Needs follow-up date"
              onChange={(v) => setForm((f) => ({ ...f, requiresFollowUp: v }))}
            />
          </div>

          <div className="flex items-start justify-between gap-3 rounded-xl border border-secondary-200 p-3">
            <div>
              <p className="text-sm font-semibold text-secondary-800">Active</p>
              <p className="text-xs text-secondary-500 mt-0.5">Inactive outcomes are hidden in the app; past visits keep them.</p>
            </div>
            <Switch checked={form.isActive} label="Active" onChange={(v) => setForm((f) => ({ ...f, isActive: v }))} />
          </div>

          <div className="flex items-center gap-2 text-xs text-secondary-500">
            Preview:
            <span
              className="px-2.5 py-1 rounded-full border text-xs font-semibold"
              style={{ color: form.color, borderColor: `${form.color}66`, backgroundColor: `${form.color}14` }}
            >
              {form.label.trim() || 'Outcome'}
            </span>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-secondary-100">
            <Button type="button" variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              {editing?.key ? 'Save' : 'Add Outcome'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (await apply(() => visitsAPI.deleteOutcome(toDelete.key), toDelete.key)) setToDelete(null);
        }}
        title="Delete outcome?"
        message={
          toDelete
            ? `"${toDelete.label}" will no longer be offered in the app.${
                toDelete.visitsCount ? ` The ${toDelete.visitsCount} past visit(s) that used it keep their outcome.` : ''
              } To hide it without deleting, switch it off instead.`
            : ''
        }
        confirmText="Delete"
        loading={!!busyKey}
      />

      <ConfirmDialog
        isOpen={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={async () => {
          if (await apply(() => visitsAPI.resetOutcomes(), 'reset')) setConfirmReset(false);
        }}
        title="Restore default outcomes?"
        message="Replaces your list with: Interested, Not Interested, Deal Closed, Follow-up Needed, Other. Past visits keep their outcome."
        confirmText="Restore defaults"
        variant="warning"
        loading={busyKey === 'reset'}
      />
    </Card>
  );
};

export default VisitOutcomes;
